// Steuerfall je Kunde (AP2c, Wunsch einvoice-04, docs/erechnung/bau/AP2c.md): Welche Steuer gilt auf Belegen an
// diesen Kunden? Inland (Standard) rechnet mit den Steuerschlüsseln der Artikel. Bei EU-Unternehmen, Drittland und
// § 13b ersetzt der Belegkern je Zeile die steuerpflichtigen Schlüssel (Kategorie S und Z) durch den Ersatzschlüssel
// der passenden Kategorie des Betriebs (K, G, AE) – Pfand folgt der Ware. EINE Quelle für Stammdaten (Vertrag,
// Prüfregeln), Belegkern (lines.ts) und Oberfläche.
//
// Rechtliche Einordnung: Annahme [U] – vor dem Echtbetrieb vom Steuerberater abzunehmen (Fragen in der Übergabe AP2c).

export const TAX_CASES = ['domestic', 'eu_business', 'export', 'reverse_charge'] as const
export type TaxCase = (typeof TAX_CASES)[number]
/** Steuerfälle mit Ersatzschlüssel */
export type ForeignTaxCase = Exclude<TaxCase, 'domestic'>

export const isTaxCase = (value: unknown): value is TaxCase => (TAX_CASES as readonly unknown[]).includes(value)

/** Bezeichnung für Auswahllisten und Verlauf */
export const TAX_CASE_LABELS: Readonly<Record<TaxCase, string>> = {
  domestic: 'Inland',
  eu_business: 'EU-Unternehmen mit USt-IdNr. (innergemeinschaftliche Lieferung)',
  export: 'Drittland (Ausfuhr)',
  reverse_charge: 'Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG)',
}

/** Kurzform für Sätze: „Der Kunde ist als … geführt“ */
export const TAX_CASE_SHORT: Readonly<Record<TaxCase, string>> = {
  domestic: 'Inlandskunde',
  eu_business: 'EU-Unternehmen',
  export: 'Kunde im Drittland',
  reverse_charge: 'Leistungsempfänger nach § 13b UStG',
}

/** Kategorie (EN 16931, BT-151) des Ersatzschlüssels je Steuerfall */
export const TAX_CASE_CATEGORY: Readonly<Record<ForeignTaxCase, 'K' | 'G' | 'AE'>> = {
  eu_business: 'K',
  export: 'G',
  reverse_charge: 'AE',
}

/** Kategorien, die ein Ersatzschlüssel sein können */
export const TAX_CASE_SUBSTITUTE_CATEGORIES = ['AE', 'K', 'G'] as const
export type TaxCaseSubstituteCategory = (typeof TAX_CASE_SUBSTITUTE_CATEGORIES)[number]

/** Wofür der Ersatzschlüssel steht – für Sätze: „… keinen Steuerschlüssel für innergemeinschaftliche Lieferungen“ */
export const TAX_CASE_SUBSTITUTE_TOPIC: Readonly<Record<ForeignTaxCase, string>> = {
  eu_business: 'innergemeinschaftliche Lieferungen',
  export: 'Ausfuhrlieferungen',
  reverse_charge: 'die Steuerschuldnerschaft des Leistungsempfängers',
}

/**
 * Kategorien, die der Ersatzschlüssel ersetzt: die steuerpflichtigen (S Regel- und ermäßigter Satz, Z Nullsatz).
 * Steuerfreie (E), nicht steuerbare (O) und bereits von Hand gewählte AE/K/G-Schlüssel bleiben, wie sie sind.
 */
export const TAX_CASE_REPLACED_CATEGORIES: readonly string[] = ['S', 'Z']

/** Kategorie des Ersatzschlüssels – null bei Inland (kein Ersatz). */
export function taxCaseCategory(taxCase: TaxCase | null | undefined): 'K' | 'G' | 'AE' | null {
  return taxCase && taxCase !== 'domestic' ? TAX_CASE_CATEGORY[taxCase] : null
}

/** Ersetzt der Steuerfall einen Schlüssel dieser Kategorie? */
export function replacesCategory(taxCase: TaxCase | null | undefined, category: string): boolean {
  return taxCaseCategory(taxCase) !== null && TAX_CASE_REPLACED_CATEGORIES.includes(category)
}
